import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { nanoid } from "@reduxjs/toolkit";
import { useImports } from "api/imports";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { COUPONS_IMPORT_DETAILS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { CouponImportDetailsItem } from "types/import/coupon";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { getItemStatus, parseImportMessage } from "./helpers";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    message: {
      whiteSpace: "break-spaces",
      color: theme.gray500,
      marginTop: 0,
      fontSize: 14,
    },
  }),
);

export const CouponsImportDetails = () => {
  const classes = useStyles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemMessage, setSelectedItemMessage] = useState("");
  const { message } = useMessage();
  const { listing, updateListing } = useListing(COUPONS_IMPORT_DETAILS_LIST_ID);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);
  const { id: importId } = useParams<{ id: string }>();
  const { useImportDetails } = useImports();

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[COUPONS_IMPORT_DETAILS_LIST_ID]?.filters || [],
  );

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      COUPONS_IMPORT_DETAILS_LIST_ID,
    );

    setFilters(filters);
  };

  const { data, error } = useImportDetails<CouponImportDetailsItem>(
    importId as string,
    {
      filters: parseFiltersForBE(getNewFiltersOnly(filters)),
      order: listing.order || "desc",
      page: listing.page,
      rows: listing.rowsPerPage,
      sortKey: listing.property,
    },
  );

  const headItems = createHeadItems([
    {
      headerText: message({ id: "coupon" }),
      property: "coupon",
      isSortable: false,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: true,
    },
    {
      headerText: message({ id: "generatedOn" }),
      property: "createdAt",
      isSortable: false,
    },
  ]);

  const getItemId = (item: CouponImportDetailsItem) =>
    "entity" in item ? item.entity.id : nanoid();

  const itemsList = useMemo(
    () =>
      data?.items.map((item) => ({
        cells: [
          {
            content: "entity" in item ? item.entity.id : "-",
            property: "coupon",
          },
          {
            content: message({ id: getItemStatus(item.status) }),
            property: "status",
          },
          {
            content: OLDate(item.createdAt),
            property: "createdAt",
          },
        ],
        id: getItemId(item),
        metadata: {
          message: "message" in item ? item.message : undefined,
        },
      })),
    [data?.items, message],
  );

  const items = data?.items ? itemsList : [];

  const handleSelectItemMessage = (message: string) => {
    const parsedMessage = parseImportMessage(message);
    setSelectedItemMessage(parsedMessage);
    setIsModalOpen(true);
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, COUPONS_IMPORT_DETAILS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfImports" }),
      path: links.importsExports,
    },
  };

  return (
    <>
      <TopHeader
        title={`${message({ id: "import" })} #${importId}`}
        clearLink={links.importsExports}
        breadcrumbs={breadcrumbs}
      />
      <Subheader>
        <FiltersV2
          data-test-id="import-details"
          properties={[
            {
              query: "status",
              label: message({ id: "status" }),
              type: "import-details-status",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
          donutLabel={message({ id: "results" })}
        />
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={COUPONS_IMPORT_DETAILS_LIST_ID}
        data-test-id="import-details"
        columns={data && headItems}
        actions={{
          body: [
            {
              action: (_, item?: any) =>
                handleSelectItemMessage(item?.metadata?.message),
              hideCondition: (item: any) => !item?.metadata?.message,
              label: message({ id: "errorDetails" }),
            },
          ],
        }}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        rows={data && items}
        sort={{
          order: listing.order || "desc",
          property: listing.property,
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
      />
      <ConfirmationModal
        open={isModalOpen}
        submitText="close"
        header="importErrorDetails"
        info={selectedItemMessage}
        className={classes.message}
        close={() => setIsModalOpen(false)}
        action={() => setIsModalOpen(false)}
        data-test-id="coupons-import-error-details-modal"
      />
    </>
  );
};
